import gsap from 'gsap'
import ScrollTrigger from 'gsap/ScrollTrigger'
import CustomEase from 'gsap/CustomEase'
import Lenis from 'lenis'

gsap.registerPlugin(ScrollTrigger, CustomEase);

export default class Contact {
    constructor(options = {}) {
        this.scroll = options.scroll
        this.contactForm = document.querySelector('.contact__form')
        this.contactLayer = document.querySelector('.contact__form__layer')
        this.closeButton = document.querySelector('.form__close')
        this.formLenis = null
        
        if (!this.contactForm) return
        
        this.tl = gsap.timeline({ paused: true })
        this.isOpen = false
        
        this.setupAnimation()
        this.addEventListeners()
    }

    setupAnimation() {
        gsap.set(this.contactForm, { display: 'none', scaleY: 0, transformOrigin: 'bottom' })
        gsap.set(this.contactLayer, { display: 'none', autoAlpha: 0 })
        gsap.set('.contact__form__header > *', { y: 30, autoAlpha: 0 })
        gsap.set('.contact__form__content__row', { y: 20, autoAlpha: 0 })
        gsap.set('.form__close', { autoAlpha: 0, scale: 0.5 })

        this.tl.set(this.contactLayer, { display: 'block' })
        this.tl.set(this.contactForm, { display: 'flex' })
        
        this.tl.to(this.contactLayer, {
            duration: 0.4,
            autoAlpha: 1,
            ease: CustomEase.create("out", "0.125, 0.425, 0.270, 1.000")
        }, 0)
        
        this.tl.to(this.contactForm, {
            duration: 0.6,
            scaleY: 1,
            ease: CustomEase.create("out", "0.125, 0.425, 0.270, 1.000")
        }, 0)

        this.tl.to('.form__close', {
            duration: 0.3,
            autoAlpha: 1,
            scale: 1,
            ease: CustomEase.create("out", "0.125, 0.425, 0.270, 1.000")
        }, '-=0.2')

        this.tl.to('.contact__form__header > *', {
            duration: 0.5,
            y: 0,
            autoAlpha: 1,
            stagger: 0.1,
            ease: CustomEase.create("out", "0.125, 0.425, 0.270, 1.000")
        }, '-=0.3')

        this.tl.to('.contact__form__content__row', {
            duration: 0.5,
            y: 0,
            autoAlpha: 1,
            stagger: 0.08,
            ease: CustomEase.create("out", "0.125, 0.425, 0.270, 1.000")
        }, '-=0.4')
    }

    handleSubmit(e) {
        e.preventDefault()
        const form = e.target
        const messageEl = form?.querySelector('.contact__form__message')
        const submitBtn = form?.querySelector('.contact__form__submit')
        const messages = window.contactFormMessages || { success: 'Message sent.', error_send: 'Could not send.' }
        const action = window.contactFormAction || (window.pilotBaseUrl ? `${window.pilotBaseUrl}/send-contact.php` : 'send-contact.php')

        const showMessage = (text, isError) => {
            if (!messageEl) return
            messageEl.textContent = text
            messageEl.hidden = false
            messageEl.classList.toggle('contact__form__message--error', isError)
        }

        if (submitBtn) {
            submitBtn.disabled = true
        }

        fetch(action, {
            method: 'POST',
            body: new FormData(form),
        })
            .then((res) => res.json())
            .then((data) => {
                if (data.ok) {
                    showMessage(messages.success, false)
                    form.reset()
                    setTimeout(() => this.close(), 2000)
                } else {
                    showMessage(data.message || messages.error_send, true)
                }
            })
            .catch(() => {
                showMessage(messages.error_send, true)
            })
            .finally(() => {
                if (submitBtn) submitBtn.disabled = false
            })
    }

    addEventListeners() {
        // Use event delegation to handle dynamically created buttons
        document.addEventListener('click', (e) => {
            const button = e.target.closest('.open-contact')
            if (button) {
                e.preventDefault()
                this.open()
            }
        })

        if (this.closeButton) {
            this.closeButton.addEventListener('click', (e) => {
                e.preventDefault()
                this.close()
            })
        }

        this.contactForm.addEventListener('click', (e) => {
            if (e.target === this.contactForm) {
                this.close()
            }
        })

        if (this.contactLayer) {
            this.contactLayer.addEventListener('click', () => {
                this.close()
            })
        }

        const form = this.contactForm?.querySelector('.contact__form__form')
        if (form) {
            form.addEventListener('submit', (e) => this.handleSubmit(e))
        }

        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape' && this.isOpen) {
                this.close()
            }
        })
    }

    initFormScroll() {
        if (!this.contactForm) return
        
        this.formLenis = new Lenis({
            wrapper: this.contactForm,
            content: this.contactForm,
            duration: 1.2,
            easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
            orientation: 'vertical',
            gestureOrientation: 'vertical',
            smoothWheel: true,
            wheelMultiplier: 1,
            smoothTouch: false,
            touchMultiplier: 2,
            infinite: false,
        })
        
        const rafForm = (time) => {
            if (this.formLenis) {
                this.formLenis.raf(time)
                requestAnimationFrame(rafForm)
            }
        }
        requestAnimationFrame(rafForm)
    }

    destroyFormScroll() {
        if (this.formLenis) {
            this.formLenis.destroy()
            this.formLenis = null
        }
    }

    open() {
        if (this.isOpen) return
        
        this.isOpen = true
        const form = this.contactForm?.querySelector('.contact__form__form')
        const messageEl = form?.querySelector('.contact__form__message')
        if (messageEl) {
            messageEl.hidden = true
            messageEl.textContent = ''
        }
        document.body.style.overflow = 'hidden'
        
        // Stop main page scroll
        if (this.scroll) {
            this.scroll.stop()
        }
        
        this.tl.play()
        
        // Initialize Lenis scroll after animation starts
        setTimeout(() => {
            this.initFormScroll()
        }, 300)
    }

    close() {
        if (!this.isOpen) return
        
        this.isOpen = false
        document.body.style.overflow = ''
        
        // Restart main page scroll
        if (this.scroll) {
            this.scroll.start()
        }
        
        // Destroy Lenis instance
        this.destroyFormScroll()
        
        gsap.to('.contact__form__content__row', {
            duration: 0.3,
            y: 20,
            autoAlpha: 0,
            stagger: 0.03,
            ease: 'power2.in'
        })

        gsap.to('.contact__form__header > *', {
            duration: 0.3,
            y: 20,
            autoAlpha: 0,
            stagger: 0.05,
            ease: 'power2.in'
        })

        gsap.to('.form__close', {
            duration: 0.2,
            autoAlpha: 0,
            scale: 0.5,
            ease: 'power2.in'
        })

        gsap.to(this.contactForm, {
            duration: 0.3,
            scaleY: 0,
            delay: 0.6,
            ease: 'power2.in'
        })

        gsap.to(this.contactLayer, {
            duration: 0.3,
            autoAlpha: 0,
            delay: 0.6,
            ease: 'power2.in',
            onComplete: () => {
                this.tl.pause(0)
                gsap.set(this.contactForm, { display: 'none' })
                gsap.set(this.contactLayer, { display: 'none' })
            }
        })
    }
}

function initContactSectionAnimation() {
    const contactSection = document.querySelector('.contact')
    
    if (!contactSection) return
    
    const customEase = CustomEase.create("out", "0.125, 0.425, 0.270, 1.000")
    
    const image = contactSection.querySelector('.contact__content img')
    const footerLeft = contactSection.querySelector('.contact__content__footer__left')
    const leftTitle = footerLeft?.querySelector('.title-tertiary')
    const leftButtons = footerLeft?.querySelectorAll('.button')
    const footerRight = contactSection.querySelector('.contact__content__footer__right')
    const rightText = footerRight?.querySelector('div:first-child')
    const rightActions = footerRight?.querySelector('.contact__content__footer__right__actions')
    
    if (image) {
        gsap.set(image, { autoAlpha: 0, scale: 1.1 })
    }
    
    if (footerLeft) {
        gsap.set(footerLeft, { autoAlpha: 0, y: 60 })
    }
    
    if (footerRight) {
        gsap.set(footerRight, { autoAlpha: 0, y: 60 })
    }
    
    if (leftTitle) {
        gsap.set(leftTitle, { autoAlpha: 0, y: 50 })
    }
    
    if (leftButtons && leftButtons.length > 0) {
        gsap.set(leftButtons, { autoAlpha: 0, y: 60 })
    }
    
    if (rightText) {
        gsap.set(rightText, { autoAlpha: 0, y: 50 })
    }
    
    if (rightActions) {
        gsap.set(rightActions, { autoAlpha: 0, y: 60 })
    }
    
    if (image) {
        gsap.to(image, {
            autoAlpha: 1,
            scale: 1,
            duration: 1.2,
            ease: customEase,
            scrollTrigger: {
                trigger: '.contact__content',
                start: 'clamp(top 85%)',
                once: true
            }
        })
    }
    
    if (footerLeft) {
        gsap.to(footerLeft, {
            autoAlpha: 1,
            y: 0,
            duration: 1,
            ease: customEase,
            scrollTrigger: {
                trigger: footerLeft,
                start: 'clamp(top 90%)',
                once: true
            }
        })
    }
    
    if (footerRight) {
        gsap.to(footerRight, {
            autoAlpha: 1,
            y: 0,
            duration: 1,
            ease: customEase,
            scrollTrigger: {
                trigger: footerRight,
                start: 'clamp(top 90%)',
                once: true
            }
        })
    }
    
    if (leftTitle) {
        gsap.to(leftTitle, {
            autoAlpha: 1,
            y: 0,
            duration: 0.9,
            ease: customEase,
            scrollTrigger: {
                trigger: leftTitle,
                start: 'clamp(top 90%)',
                once: true
            }
        })
    }
    
    if (leftButtons && leftButtons.length > 0) {
        leftButtons.forEach((button, index) => {
            gsap.to(button, {
                autoAlpha: 1,
                y: 0,
                duration: 0.9,
                delay: index * 0.1,
                ease: customEase,
                scrollTrigger: {
                    trigger: footerLeft,
                    start: 'clamp(top 90%)',
                    once: true
                }
            })
        })
    }
    
    if (rightText) {
        gsap.to(rightText, {
            autoAlpha: 1,
            y: 0,
            duration: 0.9,
            ease: customEase,
            scrollTrigger: {
                trigger: rightText,
                start: 'clamp(top 90%)',
                once: true
            }
        })
    }
    
    if (rightActions) {
        gsap.to(rightActions, {
            autoAlpha: 1,
            y: 0,
            duration: 0.9,
            ease: customEase,
            scrollTrigger: {
                trigger: rightActions,
                start: 'clamp(top 90%)',
                once: true
            }
        })
    }
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initContactSectionAnimation)
} else {
    initContactSectionAnimation()
}